// The voice note's pure decisions on the web side: which encoder a browser gets, what the file is
// called, what a microphone refusal means. Unit-tested in voiceFormat.test.ts.
//
// The format rule (VoiceRecorder.swift): AAC in .m4a, mono, 44.1 kHz, 64 kbps — what AVFoundation,
// Android's MediaPlayer and every browser play natively. The web reaches it two ways, and has an
// honest fallback for browsers that ship no AAC encoder at all (Firefox; Chrome on Linux):
//   1. WebCodecs AudioEncoder 'mp4a.40.2' (Chrome/Edge on Windows/macOS/Android, Safari 26+) — the
//      microphone's PCM encoded here and written as a progressive MP4 by mp4Audio.ts;
//   2. MediaRecorder 'audio/mp4;codecs=mp4a.40.2' (older Safari) — its fragmented MP4 re-muxed into
//      a progressive one (mp4Audio.remuxFragmented);
//   3. MP3 (LAME in JS, lazy-loaded): not the apps' format, but the one format all three players —
//      AVAudioPlayer, Android MediaPlayer, <audio> — still play. Opus/WebM would not play on Apple.
//   4. Opus via MediaRecorder only if even AudioWorklet is missing (plays on Android and the web,
//      NOT on the iPhone/iPad/Mac) — last resort, never chosen by a current browser.

export type VoiceEncoderKind = 'aac-webcodecs' | 'aac-mediarecorder' | 'mp3' | 'opus-mediarecorder';

export interface VoiceEncoderChoice {
  kind: VoiceEncoderKind;
  mimeType: string;
  extension: string;
}

export interface VoiceCapabilities {
  /** AudioEncoder.isConfigSupported({codec:'mp4a.40.2', 1 ch, 44.1/48 kHz}).supported */
  aacEncoder: boolean;
  /** MediaRecorder.isTypeSupported('audio/mp4;codecs=mp4a.40.2') */
  mediaRecorderAac: boolean;
  /** AudioWorkletNode exists (PCM capture for the WebCodecs and MP3 paths). */
  audioWorklet: boolean;
  /** MediaRecorder.isTypeSupported('audio/webm;codecs=opus') */
  mediaRecorderOpus: boolean;
}

/** The apps' bitrate (AVEncoderBitRateKey / setAudioEncodingBitRate). */
export const VOICE_BITRATE = 64_000;
/** The apps' sample rate; a context that cannot resample the microphone keeps its own (48 kHz). */
export const VOICE_SAMPLE_RATE = 44_100;
/** Takes shorter than this are mis-taps and are discarded (VoiceRecorder.minDuration). */
export const VOICE_MIN_DURATION = 0.7;

export function chooseVoiceEncoder(caps: VoiceCapabilities): VoiceEncoderChoice | null {
  if (caps.aacEncoder && caps.audioWorklet) return { kind: 'aac-webcodecs', mimeType: 'audio/mp4', extension: 'm4a' };
  if (caps.mediaRecorderAac) return { kind: 'aac-mediarecorder', mimeType: 'audio/mp4', extension: 'm4a' };
  if (caps.audioWorklet) return { kind: 'mp3', mimeType: 'audio/mpeg', extension: 'mp3' };
  if (caps.mediaRecorderOpus) return { kind: 'opus-mediarecorder', mimeType: 'audio/webm', extension: 'webm' };
  return null;
}

/**
 * "nota-de-voz-20261011T183005Z.m4a" — the Apple recorder's name: ISO 8601 in UTC with the ":" and
 * "-" taken out (ISO8601DateFormatter().string(from:) without fractional seconds).
 */
export function voiceNoteName(date: Date, extension: string): string {
  const stamp = date.toISOString().replace(/\.\d{3}Z$/, 'Z').replace(/[:-]/g, '');
  return `nota-de-voz-${stamp}.${extension}`;
}

export type MicError = 'denied' | 'noDevice' | 'busy' | 'unsupported' | 'failed';

/** What a getUserMedia rejection means for the reader (the DOMException's name decides). */
export function micErrorKind(error: unknown): MicError {
  const name = (error as { name?: string } | null)?.name ?? '';
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'denied';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'noDevice';
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'busy';
    case 'TypeError':
    case 'NotSupportedError':
      return 'unsupported';
    default:
      return 'failed';
  }
}

export const micErrorKey: Record<MicError, string> = {
  denied: 'web.voice.denied',
  noDevice: 'web.voice.noDevice',
  busy: 'web.voice.busy',
  unsupported: 'web.voice.unsupported',
  failed: 'web.voice.failed',
};

/** The card's speed chip (AttachmentViews.swift rateLabel). */
export function rateLabel(rate: number): string {
  if (rate >= 2) return '2×';
  if (rate >= 1.5) return '1.5×';
  if (rate >= 1.25) return '1.25×';
  return '1×';
}

/** Float PCM [-1, 1] → 16-bit (what LAME takes), clamped. */
export function floatTo16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/** RMS of a frame mapped to 0…1 on a -60…0 dBFS scale — the strip's level meter. */
export function meterLevel(frame: Float32Array): number {
  if (!frame.length) return 0;
  let sum = 0;
  for (let i = 0; i < frame.length; i++) sum += frame[i] * frame[i];
  const rms = Math.sqrt(sum / frame.length);
  if (rms <= 0) return 0;
  const db = 20 * Math.log10(rms);
  return Math.max(0, Math.min(1, (db + 60) / 60));
}
